/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-09
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Upgrade as UpgradeIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Subscription plan suggestion interface
 */
interface PlanSuggestion {
  plan_slug: string;
  plan_name: string;
  price_monthly: number;
  studios_limit?: number;
  users_limit?: number;
  features?: string[];
}

/**
 * Current usage information
 */
interface CurrentUsage {
  studios?: number;
  users?: number;
  studios_limit?: number;
  users_limit?: number;
}

/**
 * Props for the UpgradePrompt component
 */
interface UpgradePromptProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  currentUsage?: CurrentUsage;
  upgradeSuggestions?: PlanSuggestion[];
  limitType?: 'studio' | 'member' | 'general';
  onUpgrade?: (planSlug: string) => void;
}

/**
 * Upgrade prompt component for subscription limit scenarios
 *
 * This component displays when users hit their subscription limits and need to upgrade.
 * It shows current usage, explains the limit, and provides upgrade options.
 */
const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  open,
  onClose,
  title,
  message,
  currentUsage,
  upgradeSuggestions = [],
  limitType = 'general',
  onUpgrade,
}) => {
  const { t } = useTranslation('billing');
  const theme = useTheme();
  const navigate = useNavigate();

  // Default titles and messages based on limit type
  const getDefaultTitle = (): string => {
    switch (limitType) {
      case 'studio':
        return t('limits.studioLimitReached');
      case 'member':
        return t('limits.memberLimitReached');
      default:
        return t('upgrade.upgradeRequired');
    }
  };

  const getDefaultMessage = (): string => {
    switch (limitType) {
      case 'studio':
        return t('limits.upgradeToCreateMore');
      case 'member':
        return t('limits.upgradeToInviteMore');
      default:
        return t('limits.upgradeToUnlock');
    }
  };

  // Format price for display
  const formatPrice = (priceInCents: number): string =>
    `$${(priceInCents / 100).toFixed(0)}`;

  // Handle upgrade button click
  const handleUpgrade = (planSlug: string) => {
    if (onUpgrade) {
      onUpgrade(planSlug);
    } else {
      // Default behavior: navigate to pricing page
      navigate('/pricing', {
        state: {
          selectedPlan: planSlug,
          upgradeContext: limitType,
        },
      });
    }
  };

  // Handle view all plans
  const handleViewAllPlans = () => {
    navigate('/pricing', {
      state: {
        upgradeContext: limitType,
      },
    });
    onClose();
  };

  // Get the best upgrade suggestion (usually the cheapest plan that solves the issue)
  const primarySuggestion = upgradeSuggestions[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              color: 'white',
            }}
          >
            <UpgradeIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {title || getDefaultTitle()}
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            size="small"
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Main message */}
        <Typography variant="body1" sx={{ mb: 3 }}>
          {message || getDefaultMessage()}
        </Typography>

        {/* Current usage display */}
        {currentUsage && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>{t('usage.currentUsage')}:</strong>
              {currentUsage.studios !== undefined && (
                <span>
                  {' '}
                  {currentUsage.studios} of {currentUsage.studios_limit || '∞'}{' '}
                  {t('usage.studios')}
                </span>
              )}
              {currentUsage.users !== undefined && (
                <span>
                  {currentUsage.studios !== undefined ? ', ' : ' '}
                  {currentUsage.users} of {currentUsage.users_limit || '∞'}{' '}
                  {t('usage.members')}
                </span>
              )}
            </Typography>
          </Alert>
        )}

        {/* Primary upgrade suggestion */}
        {primarySuggestion && (
          <Box
            sx={{
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              p: 3,
              mb: 3,
              backgroundColor: `${theme.palette.primary.light}10`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                label={t('plans.recommended')}
                color="primary"
                size="small"
                icon={<TrendingUpIcon />}
              />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {primarySuggestion.plan_name}
            </Typography>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              {formatPrice(primarySuggestion.price_monthly)}
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {t('plans.perMonth')}
              </Typography>
            </Typography>

            {/* Plan limits */}
            <Box sx={{ mb: 2 }}>
              {primarySuggestion.studios_limit !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  •{' '}
                  {primarySuggestion.studios_limit === 0
                    ? t('usage.unlimited')
                    : t('features.upToStudios', {
                        count: primarySuggestion.studios_limit,
                      })}{' '}
                  {t('features.studios')}
                </Typography>
              )}
              {primarySuggestion.users_limit !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  •{' '}
                  {primarySuggestion.users_limit === 0
                    ? t('usage.unlimited')
                    : t('features.upToUsers', {
                        count: primarySuggestion.users_limit,
                      })}{' '}
                  {t('features.users')}
                </Typography>
              )}
              {primarySuggestion.features?.map(feature => (
                <Typography
                  key={feature}
                  variant="body2"
                  color="text.secondary"
                >
                  • {feature}
                </Typography>
              ))}
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={() => handleUpgrade(primarySuggestion.plan_slug)}
              sx={{ mt: 1 }}
            >
              {t('upgrade.upgradeTo', { plan: primarySuggestion.plan_name })}
            </Button>
          </Box>
        )}

        {/* Additional upgrade suggestions */}
        {upgradeSuggestions.length > 1 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {t('upgrade.otherOptions')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {upgradeSuggestions.slice(1).map(suggestion => (
                <Box
                  key={suggestion.plan_slug}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {suggestion.plan_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatPrice(suggestion.price_monthly)}/month
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleUpgrade(suggestion.plan_slug)}
                  >
                    {t('plans.selectPlan')}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleViewAllPlans} color="primary">
          {t('usage.viewAllPlans')}
        </Button>
        <Button onClick={onClose} variant="outlined">
          {t('plans.notNow')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePrompt;
